// Generates the branded "Before & After" composite that the cut timer
// produces at the end of a cut. Two layouts:
//
//   'portrait' (default) — 1080×1920, before stacked ON TOP of after
//     with a thin gold seam between, then a pre-baked picture-frame
//     overlay (BEFORE/AFTER banners + Busy mascot in the bottom-right
//     pocket). Optimized for Instagram Reels covers, IG Stories, TikTok
//     thumbnails, YouTube Shorts.
//
//   'square' — 1080×1080, before stacked ON TOP of after (same vertical
//     split as portrait, just at 1:1 instead of 9:16). Same frame
//     overlay style. Better for IG feed posts and X / Facebook
//     timelines.
//
// The frame assets (frame-portrait.png + frame-square.png) carry all the
// chrome that earlier versions of this file drew on the canvas directly:
// "BEFORE" + "AFTER" graffiti banners, gold border, barber-pole striped
// corners, and the Busy mascot in the bottom-right circle. Removing the
// canvas-drawn header / half labels / corner logo means the photo area
// is now the FULL canvas — the frame border sits ON TOP of the photo
// (its corners overlap the photo) and the photo shows through the
// transparent middle of the frame.
//
// All canvas — no server work, instant generation, works offline. The
// output Blob is what the share modal hands to the Web Share API.

export type ComposeLayout = 'portrait' | 'square';

/** Optional high-fidelity input — when provided, the composer draws
 *  directly from the native (uncompressed) camera source and the
 *  user's crop area in source-pixel space, skipping the lossy
 *  cropped-thumbnail blob. Used by the cut timer's transformation
 *  flow so the output composite hits ONE JPEG encoding (the
 *  composite itself) instead of two (cropped thumbnail + composite).
 *  Other callers (client gallery share, etc.) keep using the
 *  standard two-blob signature. */
export type NativeSource = {
  /** Original camera File/Blob — full resolution, original device
   *  JPEG quality (typically 95+). */
  source: Blob;
  /** Crop rectangle in NATIVE source-pixel coordinates. Matches the
   *  PhotoCropArea type the PhotoCaptureModal emits. */
  crop: { x: number; y: number; width: number; height: number };
};

const SQUARE_SIZE = 1080;
const PORTRAIT_W = 1080;
const PORTRAIT_H = 1920;
// Thin gold seam separating the BEFORE / AFTER photo halves. The frame
// border around the outside is the picture-frame overlay; this seam is
// the interior divider between the two photos so the eye reads them as
// two distinct moments rather than one continuous image.
const SEAM_WIDTH = 6;
// JPEG quality for the final composite. 0.95 is the right level for an
// output that's been through one prior compression pass already
// (PhotoCaptureModal writes JPEG 0.94 for the source halves). Two-pass
// JPEG compounds quality loss — the slightly higher final pass
// compensates without bloating the share blob meaningfully.
const OUTPUT_QUALITY = 0.95;

const GOLD_500 = '#d4a942';

// Picture-frame overlay assets — pre-baked at exact target dimensions so
// drawImage is a single 1:1 blit with no runtime scaling.
const FRAME_PORTRAIT_PATH = '/assets/frame-portrait.png';
const FRAME_SQUARE_PATH = '/assets/frame-square.png';

/** Composite from two photo blobs. Default layout = 'portrait' (9:16
 *  stacked vertical) which matches the video-capture flow's 9:16 aspect.
 *  Returns a JPEG blob ready for share / storage upload.
 *
 *  `beforeNative` / `afterNative` are optional — when provided, the
 *  composer uses the native source + crop area instead of the cropped
 *  blob. Single-pass JPEG encoding (composite only) instead of two
 *  passes (cropped thumbnail + composite). The cropped blobs are
 *  still required because they're the fallback if native sources are
 *  unavailable. */
export async function composeBeforeAfter(
  beforeBlob: Blob,
  afterBlob: Blob,
  layout: ComposeLayout = 'portrait',
  beforeNative?: NativeSource | null,
  afterNative?: NativeSource | null,
): Promise<Blob> {
  const framePath = layout === 'portrait' ? FRAME_PORTRAIT_PATH : FRAME_SQUARE_PATH;
  // Resolve each half to either:
  //   - a CroppedImage from the native source (when available) — full
  //     resolution from the camera blob, cropped on the fly
  //   - the standard <img> loaded from the cropped blob (legacy / fallback)
  // The frame overlay is decorative — composite still works if the asset
  // fails to load (catches → null, composer skips the overlay draw).
  const [beforeRes, afterRes, frameImg] = await Promise.all([
    resolveHalf(beforeBlob, beforeNative),
    resolveHalf(afterBlob, afterNative),
    loadImage(framePath).catch(() => null),
  ]);

  if (layout === 'portrait') {
    return composePortrait(beforeRes, afterRes, frameImg);
  }
  return composeSquare(beforeRes, afterRes, frameImg);
}

/** Internal half-source representation. The composer uses drawCovered
 *  which accepts an image-like + an optional source-rect override —
 *  for native crops, we draw the crop rect out of the full image
 *  rather than the whole thing. */
type HalfSource = {
  img: HTMLImageElement;
  /** When set, only this region of `img` (in img-native coords) gets
   *  drawn into the composite half. When null, the full image is used. */
  sourceRect: { x: number; y: number; width: number; height: number } | null;
};

async function resolveHalf(
  fallbackBlob: Blob,
  native: NativeSource | null | undefined,
): Promise<HalfSource> {
  if (native) {
    try {
      const img = await blobToImage(native.source);
      // Defensive: if the crop rect somehow exceeds the image bounds
      // (shouldn't happen — comes from react-easy-crop on the same
      // source), clamp to image bounds so we don't draw black bars.
      const sourceRect = {
        x: Math.max(0, Math.min(native.crop.x, img.naturalWidth)),
        y: Math.max(0, Math.min(native.crop.y, img.naturalHeight)),
        width: Math.min(native.crop.width, img.naturalWidth - native.crop.x),
        height: Math.min(native.crop.height, img.naturalHeight - native.crop.y),
      };
      return { img, sourceRect };
    } catch {
      // Native source failed to load — fall through to the cropped
      // blob below so the composite still produces an output.
    }
  }
  const img = await blobToImage(fallbackBlob);
  return { img, sourceRect: null };
}

// 1080×1920 portrait — before stacked over after with a gold seam,
// then the picture-frame overlay on top (BEFORE/AFTER banners + mascot
// pocket). Optimized for Reels/Stories/TikTok.
async function composePortrait(
  before: HalfSource,
  after: HalfSource,
  frameImg: HTMLImageElement | null,
): Promise<Blob> {
  const W = PORTRAIT_W;
  const H = PORTRAIT_H;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('photoCompose: 2D context unavailable');

  // Photos fill the entire canvas, stacked vertically with the gold
  // seam between. The frame overlay (drawn last) carries the BEFORE /
  // AFTER labeling, so we don't need any text/header on the canvas.
  const halfHeight = (H - SEAM_WIDTH) / 2;
  drawHalf(ctx, before, 0, 0, W, halfHeight);
  drawHalf(ctx, after, 0, halfHeight + SEAM_WIDTH, W, halfHeight);

  // Horizontal gold seam between halves
  ctx.fillStyle = GOLD_500;
  ctx.fillRect(0, halfHeight, W, SEAM_WIDTH);

  // Picture-frame overlay on top of everything
  if (frameImg) ctx.drawImage(frameImg, 0, 0, W, H);

  return await canvasToBlob(canvas, 'image/jpeg', OUTPUT_QUALITY);
}

// 1080×1080 square — same vertical split as portrait so it lines up
// with the matching square frame asset (which also has BEFORE on top,
// AFTER on bottom). Kept for callers that explicitly request square
// (IG feed posts, X / Facebook timelines).
async function composeSquare(
  before: HalfSource,
  after: HalfSource,
  frameImg: HTMLImageElement | null,
): Promise<Blob> {
  const SIZE = SQUARE_SIZE;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('photoCompose: 2D context unavailable');

  const halfHeight = (SIZE - SEAM_WIDTH) / 2;
  drawHalf(ctx, before, 0, 0, SIZE, halfHeight);
  drawHalf(ctx, after, 0, halfHeight + SEAM_WIDTH, SIZE, halfHeight);

  ctx.fillStyle = GOLD_500;
  ctx.fillRect(0, halfHeight, SIZE, SEAM_WIDTH);

  if (frameImg) ctx.drawImage(frameImg, 0, 0, SIZE, SIZE);

  return await canvasToBlob(canvas, 'image/jpeg', OUTPUT_QUALITY);
}

/** Draw one half of the composite. Handles the two source modes:
 *  - native-source crop: the half is a region of a full-resolution
 *    camera image. Draw the crop region directly with object-cover
 *    fit into the destination half. ONE drawImage call from full
 *    res to composite — no intermediate JPEG re-encoding.
 *  - cropped-blob fallback: standard object-cover from the cropped
 *    (square) thumbnail blob. Used when no native source was
 *    captured. */
function drawHalf(
  ctx: CanvasRenderingContext2D,
  half: HalfSource,
  dx: number,
  dy: number,
  dWidth: number,
  dHeight: number,
) {
  const img = half.img;
  if (half.sourceRect) {
    // Native path: source rect is in img native pixels. Apply
    // object-cover inside that rect: pick the larger of horizontal /
    // vertical scale so the destination fills with no letterboxing,
    // and center-crop the overflow on the longer axis.
    const sr = half.sourceRect;
    const scale = Math.max(dWidth / sr.width, dHeight / sr.height);
    const sw = dWidth / scale;
    const sh = dHeight / scale;
    const sx = sr.x + (sr.width - sw) / 2;
    const sy = sr.y + (sr.height - sh) / 2;
    ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dWidth, dHeight);
  } else {
    drawCovered(ctx, img, dx, dy, dWidth, dHeight);
  }
}

function drawCovered(
  ctx: CanvasRenderingContext2D,
  img: HTMLImageElement,
  dx: number,
  dy: number,
  dWidth: number,
  dHeight: number,
) {
  // Object-cover: pick the smaller scale so the dest is fully filled,
  // crop the overflow horizontally + vertically (centered).
  const scale = Math.max(dWidth / img.width, dHeight / img.height);
  const sw = dWidth / scale;
  const sh = dHeight / scale;
  const sx = (img.width - sw) / 2;
  const sy = (img.height - sh) / 2;
  ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dWidth, dHeight);
}

function blobToImage(blob: Blob): Promise<HTMLImageElement> {
  const url = URL.createObjectURL(blob);
  return loadImage(url).finally(() => URL.revokeObjectURL(url));
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

function canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('canvas.toBlob returned null'))),
      type,
      quality,
    );
  });
}
